"use client"

import * as React from "react"

import { cn } from "../../lib/utils"
import {
  HorizontalScrollControls,
  HorizontalScrollViewport,
  useHorizontalScrollAffordances,
  useHorizontalScrollAlignEnd,
} from "./horizontal-scroll-controls"

export type {
  HorizontalScrollAffordances,
  HorizontalScrollControlsLayout,
  HorizontalScrollControlsProps,
} from "./horizontal-scroll-controls"
export {
  HORIZONTAL_SCROLL_STEP_PX,
  HorizontalScrollControls,
  HorizontalScrollViewport,
  horizontalScrollViewportClassName,
  useHorizontalScrollAffordances,
  useHorizontalScrollAlignEnd,
  useHorizontalScrollItemIntoView,
} from "./horizontal-scroll-controls"

export interface HorizontalScrollRegionProps {
  children: React.ReactNode
  /** Prefix for prev/next `aria-label`s (e.g. "Views"). */
  ariaLabel?: string
  className?: string
  scrollClassName?: string
  /** Scroll to the trailing edge when content overflows (breadcrumb trails). */
  alignEnd?: boolean
  /**
   * `toolbar` — fixed 32px row (tabs, breadcrumbs).
   * `content` — grows with tall children (KPI scroll strip).
   */
  fit?: "toolbar" | "content"
  /**
   * `split` — prev before content, next after.
   * `group-end` / `group-start` — shared {@link HorizontalScrollControls} after / before content.
   */
  controlsLayout?: "split" | "group-end" | "group-start"
}

/**
 * Horizontal scroll container with {@link HorizontalScrollControls} when content overflows.
 * Use for view-tab toolbars, breadcrumb trails, tab lists, and other clipped rows.
 */
export function HorizontalScrollRegion({
  children,
  ariaLabel = "Scroll",
  className,
  scrollClassName,
  alignEnd = false,
  fit = "toolbar",
  controlsLayout = "group-end",
}: HorizontalScrollRegionProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, overflowing, scrollPrev, scrollNext } =
    useHorizontalScrollAffordances(scrollRef)

  useHorizontalScrollAlignEnd(scrollRef, alignEnd, [children])

  const isGrouped =
    controlsLayout === "group-end" || controlsLayout === "group-start"
  const groupLeading = controlsLayout === "group-start"

  const controlProps = {
    ariaLabel,
    canScrollLeft,
    canScrollRight,
    onScrollPrev: scrollPrev,
    onScrollNext: scrollNext,
  }

  return (
      <div
        data-slot="horizontal-scroll-region"
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          fit === "toolbar" ? "h-8" : "min-h-0 items-stretch py-0.5",
          fit === "content" && "gap-2.5",
          className,
        )}
      >
      {isGrouped && groupLeading && overflowing ? (
        <HorizontalScrollControls {...controlProps} layout="group" className="self-center" />
      ) : null}
      {!isGrouped && overflowing ? (
        <HorizontalScrollControls {...controlProps} layout="split-prev" />
      ) : null}
      <HorizontalScrollViewport
        ref={scrollRef}
        label={ariaLabel}
        className={scrollClassName}
      >
        {children}
      </HorizontalScrollViewport>
      {isGrouped && !groupLeading && overflowing ? (
        <HorizontalScrollControls {...controlProps} layout="group" className="self-center" />
      ) : null}
      {!isGrouped && overflowing ? (
        <HorizontalScrollControls {...controlProps} layout="split-next" />
      ) : null}
    </div>
  )
}
